import React, { useState, useEffect, useRef } from 'react';
import { Drawer } from 'aha-components';
import { isMobile } from '@/biz/tool';
import { useWebModalStore } from '@/hooks/useWebModalStore';
import { useDetailDataStore } from '@/hooks/useDetailDataStore';
import { useMultiplePricesStore } from '@/hooks/useMultiplePricesStore';
import { OrderStatus } from '@/interface/order';
import { useTranslation } from 'react-i18next';
import BottomDrawer from '../BottomDrawer';
import AnalysisContent from './AnalysisContent';
import PriceContent from './PriceContent';
import MobileConst from './MobileConst';

// PC端 接单总弹窗

const ModalShowAnInterest: React.FC = () => {
  const { t } = useTranslation();
  const {
    showModalTakeOrder,
    setShowModalTakeOrder,
    setShowModalAcceptAhaPicksOffer,
    setThisLoading,
    setTakeOrderAudienceView
  } = useWebModalStore();
  const { setScreeningQuestions } = useMultiplePricesStore();
  const { detailData } = useDetailDataStore();
  const [selectCountryError, setSelectCountryError] = useState(false);
  const [audienceError, setAudienceError] = useState(false);
  const [isPriceValid, setIsPriceValid] = useState(true);
  // 弹窗刚打开时需要重置 answer；detail 刷新时要保留已填内容
  const resetScreeningAnswersOnSyncRef = useRef(false);

  // 关闭弹窗时，清空状态；打开时标记下次同步需要重置答案
  useEffect(() => {
    if (!showModalTakeOrder) {
      setShowModalAcceptAhaPicksOffer(false);
      setSelectCountryError(false);
      setAudienceError(false);
      setIsPriceValid(true);
      setThisLoading(false);
      setTakeOrderAudienceView(null);
      const store = useMultiplePricesStore.getState();
      store.setCompetitivePriceDetail(null);
      store.setNewOrderPrice('');
      store.setScreeningQuestions([]);
      store.setScreeningQuestionsError(false);
      resetScreeningAnswersOnSyncRef.current = false;
      return;
    }

    resetScreeningAnswersOnSyncRef.current = true;
  }, [showModalTakeOrder]);

  // 同步 screening questions：
  // - 刚打开弹窗：用空 answer 初始化
  // - 打开后 questions 晚到：补初始化
  // - detail 刷新：保留已填 answer，避免被清空
  useEffect(() => {
    if (!showModalTakeOrder) return;

    const campaignScreeningQuestions = detailData?.campaign?.screeningQuestions;
    if (!campaignScreeningQuestions?.length) {
      // campaign 已加载且确实没有题目时才清空；detail 未就绪时不要动
      if (detailData?.campaign) {
        setScreeningQuestions([]);
        resetScreeningAnswersOnSyncRef.current = false;
      }
      return;
    }

    const existing = useMultiplePricesStore.getState().screeningQuestions;
    const shouldResetAnswers = resetScreeningAnswersOnSyncRef.current;
    resetScreeningAnswersOnSyncRef.current = false;

    setScreeningQuestions(
      campaignScreeningQuestions.map((question: any, index: number) => ({
        ...question,
        answer: shouldResetAnswers
          ? (question?.answer ?? '')
          : (existing[index]?.answer ?? question?.answer ?? '')
      }))
    );
  }, [
    showModalTakeOrder,
    detailData?.campaign,
    detailData?.campaign?.screeningQuestions,
    setScreeningQuestions
  ]);

  const handleClose = () => {
    setShowModalTakeOrder(false);
  };

  if (!detailData?.order?.status.includes(OrderStatus.Invited)) {
    return null;
  }

  return (
    <>
      {isMobile() ? (
        <BottomDrawer
          title={
            <div className="mt-3 text-lg font-semibold">
              {t('modal.modal_take_order.title', {
                defaultValue: 'Submit interest'
              })}
            </div>
          }
          visible={showModalTakeOrder}
          handleClose={handleClose}
          isShowFooter={false}
        >
          <div className="flex flex-col overflow-y-auto">
            <MobileConst />
          </div>
        </BottomDrawer>
      ) : (
        <Drawer
          width={1000}
          title={
            <div className="text-2xl leading-9 font-semibold">
              {t('modal.modal_take_order.title', {
                defaultValue: 'Submit interest'
              })}
            </div>
          }
          open={showModalTakeOrder}
          onClose={handleClose}
          bodyStyle={{
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            paddingTop: '0px',
            paddingBottom: '0px'
          }}
          placement="right"
        >
          <div className="flex flex-1 min-h-0 min-w-0 overflow-hidden">
            <AnalysisContent
              selectCountryError={selectCountryError}
              setSelectCountryError={setSelectCountryError}
              audienceError={audienceError}
              setAudienceError={setAudienceError}
              onPriceValidChange={setIsPriceValid}
            />
            <PriceContent
              setSelectCountryError={setSelectCountryError}
              setAudienceError={setAudienceError}
              isPriceValid={isPriceValid}
            />
          </div>
        </Drawer>
      )}
    </>
  );
};

export default ModalShowAnInterest;
